import React, { Suspense, useMemo, useState } from 'react';
import ChevronDownIcon from 'components/Icons/ChevronDownIcon';
import ShareIcon from 'components/Icons/ShareIcon';
import { ActiveTradesStats } from 'store/services/trade/tradeApi';
import { TRADE_PANEL_HEIGHT } from 'constants/trades';
import PanelErrorBoundary from './PanelErrorBoundary';
import { getReadyProgress, getTransitProgress } from './rowProgress';
import TradeTabStrip, { TabFigure } from './TradeTabStrip';
import { PanelDirection, PanelStatus } from './types';
import { TradePanelState } from './useTradePanelState';
import './TradePanelSection.css';

// Its own chunk: mobile starts collapsed, so many sessions never need the map
// outline or the chart code at all.
const TradeTabPanel = React.lazy(() => import('./TradeTabPanel'));
// Also lazy: it draws the map, and most sessions never open it
const TradeMapShareModal = React.lazy(() => import('./tradeMapCard/TradeMapShareModal'));

export interface PanelHeaderProps {
  collapseControl: React.ReactNode;
  children: React.ReactNode;
}

interface Props {
  direction: PanelDirection;
  /** The page's black header block: ActiveTradesHeader or ActiveReceivingHeader */
  Header: React.ComponentType<PanelHeaderProps>;
  /** Rendered between the header block and the tabs (the /sending shipping guide banner) */
  between?: React.ReactNode;
  activeStatus: PanelStatus;
  onStatusChange: (status: PanelStatus) => void;
  stats?: ActiveTradesStats;
  /** From useTradePanelState */
  panel: TradePanelState;
  searchActive: boolean;
}

const STATUSES: PanelStatus[] = ['ready', 'transit', 'completed', 'cancelled'];

const buildFigures = (stats?: ActiveTradesStats): Record<PanelStatus, TabFigure> | undefined => {
  if (!stats) return undefined;
  const figures = {} as Record<PanelStatus, TabFigure>;
  STATUSES.forEach((status) => {
    const aggregate = stats.byStatus?.[status];
    // A backend without byStatus still has the search-blind counts: badges, no points
    figures[status] = aggregate
      ? { count: aggregate.tradeCount, points: aggregate.points }
      : { count: stats.statusCounts[status] ?? 0 };
  });
  return figures;
};

/**
 * Tab strip + header block for /sending and /receiving, in both layouts.
 * Expanded: tabs, then the black block holding the panel. Collapsed: the block
 * is gone and the figures ride on the tabs, with the overdue count and Expand
 * pushed right.
 */
const TradePanelSection: React.FC<Props> = ({
  direction,
  Header,
  between,
  activeStatus,
  onStatusChange,
  stats,
  panel,
  searchActive,
}) => {
  const { data: panelData, isLoading: panelLoading, broken: panelBroken, collapsed: isCollapsed } = panel;
  const onCollapsedChange = panel.setCollapsed;

  const figures = useMemo(() => buildFigures(stats), [stats]);

  // Red, and only these: an expired ship window the viewer owes (outbound), or
  // a package past its delivery estimate (inbound). Outbound is someone waiting
  // on the viewer; inbound is the viewer waiting on the post, so it is "overdue".
  const overdueCount = useMemo(() => {
    if (!panelData) return 0;
    const now = Date.now();
    return panelData.live.filter((row) =>
      direction === 'outbound'
        ? row.status === 'ready' && getReadyProgress(row, now).band === 'overdue'
        : row.status === 'transit' && getTransitProgress(row, now).band === 'overdue'
    ).length;
  }, [panelData, direction]);

  const [shareOpen, setShareOpen] = useState(false);

  // Share and Expand/Collapse: one button shape, Share on the left. Collapse is
  // the one that removes content, so it sits at the outer edge. The pair renders
  // in BOTH layouts, so Share survives collapsing the panel: someone who tidied
  // the panel away may still want to post the map.
  //
  // The chevron is components/Icons/ChevronDownIcon: the vertical sibling of the
  // left nav's real collapse toggle (ChevronLeftIcon) and the glyph ProfileMenu
  // rotates. The site has no single collapse glyph (five different marks), so
  // this is a choice, not a match. One asset, rotated 180deg; never swapped.
  const headControls = (expanded: boolean) => (
    <div className="ttp-head-controls" data-testid="ttp-head-controls">
      {!panelBroken && (
        <button
          type="button"
          className="ttp-head-btn"
          data-testid="ttp-share-control"
          aria-label="Share your trade map"
          onClick={() => setShareOpen(true)}
        >
          <ShareIcon size={14} color="currentColor" />
          {/* The words are dropped in the collapsed strip under 768px, where the tabs need the width */}
          <span className="ttp-head-btn-word">Share</span>
        </button>
      )}
      {(expanded || !panelBroken) && (
        <button
          type="button"
          className="ttp-head-btn"
          data-testid="ttp-collapse-control"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          onClick={() => onCollapsedChange(expanded)}
        >
          <span className="ttp-head-btn-word">{expanded ? 'Collapse' : 'Expand'}</span>
          <ChevronDownIcon
            className={`ttp-head-chevron ${expanded ? 'ttp-head-chevron--expanded' : 'ttp-head-chevron--collapsed'}`}
            size={11}
            color="currentColor"
          />
        </button>
      )}
    </div>
  );

  const collapsedTrailing = (
    <>
      {overdueCount > 0 && (
        <span className="ttp-overdue-count" data-testid="ttp-overdue-count">
          {overdueCount.toLocaleString('en-US')} {direction === 'outbound' ? 'waiting on you' : 'overdue'}
        </span>
      )}
      {headControls(false)}
    </>
  );

  return (
    <>
      {/* Tabs first: pick a tab, then look at what it shows */}
      <TradeTabStrip
        direction={direction}
        activeStatus={activeStatus}
        onChange={onStatusChange}
        figures={figures}
        trailing={isCollapsed ? collapsedTrailing : undefined}
      />
      {!isCollapsed && (
        <div className="ttp-block-host" data-status={activeStatus} style={{ '--ttp-height': `${TRADE_PANEL_HEIGHT}px` } as React.CSSProperties}>
          <Header collapseControl={headControls(true)}>
            <PanelErrorBoundary onError={panel.onRenderFailed}>
              <Suspense fallback={<div className="ttp-panel-fallback" />}>
                <TradeTabPanel
                  direction={direction}
                  status={activeStatus}
                  data={panelData}
                  isLoading={panelLoading}
                  searchActive={searchActive}
                  search={panel.search}
                  // The badge figure itself; the panel's own total only while stats are loading
                  badgeCount={
                    figures?.[activeStatus]?.count ??
                    (activeStatus === 'ready' ? panelData?.liveReadyTotal : panelData?.liveTransitTotal) ??
                    0
                  }
                />
              </Suspense>
            </PanelErrorBoundary>
          </Header>
        </div>
      )}
      {between}
      {/* Mounted only while open, so both directions are fetched on the click and not before */}
      {shareOpen && (
        <Suspense fallback={null}>
          <TradeMapShareModal onClose={() => setShareOpen(false)} />
        </Suspense>
      )}
    </>
  );
};

export default TradePanelSection;
